import React from 'react';
import {useErrorBoundary} from 'react-error-boundary';
import {AssistantType, AutoFlowError} from '@taopix/taopix-core';
import {
  AssetMetaInterface,
  AutoFillArgs,
  AutoFillResponse,
  ReducerState,
} from '@taopix/taopix-core/dist/types/types';
import {useAssistant} from '../../Context/Assistant/AssistantContext';
import {MediaGalleryProps} from '../../MediaGallery/MediaGallery';
import {useTaopix} from '../../Context/TaopixContext';
import {BaseAssistant} from './BaseAssistant';
import {useExperience} from '../../Experience/ExperienceProvider';
import {AssistantActions} from '../../Context/Assistant/AssistantActionTypes';
import {useUserFeedback} from '../../Context/UserFeedback/UserFeedbackContext';
import {UserFeedbackActions} from '../../Context/UserFeedback/UserFeedbackActionTypes';
import {AssistantCommonProps, AssistantHeaderProps, AssistantNavigationProps} from '../Assistant';

type StandardAssistantProps = {
  assets: AssetMetaInterface[];
  mediaGalleryProps: MediaGalleryProps;
  onAfterRun: () => void;
} & AssistantCommonProps &
  AssistantNavigationProps &
  AssistantHeaderProps;

export const StandardAssistant = ({...props}: StandardAssistantProps) => {
  const taopix = useTaopix();
  const assistant = useAssistant();
  const userFeedback = useUserFeedback();
  const {experience} = useExperience();
  const {showBoundary} = useErrorBoundary();
  const stateManager = taopix.core.get('stateManager');

  const run = (): Promise<void | (ReducerState & AutoFillResponse)> => {
    const args: AutoFillArgs = {
      save: true,
      showLoading: false,
      createPreviewThumbnail: false,
      coverItems: assistant.formatCoverItems(),
      checkImageUsage: false,
      fromCreation: true,
      sortOrder: experience.WIZARD.addPicturesStep.defaultSortOrder,
      sortDirection: 'asc',
      includeCovers: !experience.WIZARD.cover1Step.allowCustomersToSelectPicturesAndText,
    };

    return stateManager
      .call<AutoFillArgs, ReducerState & AutoFillResponse>('autofill', args)
      .then(response => {
        assistant.dispatch(AssistantActions.setHistoryID(response.historyAction));
        return response;
      })
      .catch(error => {
        if (error instanceof AutoFlowError) {
          userFeedback.dispatch(
            UserFeedbackActions.setErrorUserFeedback({
              title: '',
              message: error.message,
              showHeader: false,
            })
          );
        } else if (500 === error?.response?.status) {
          // Display our error from the server.
          showBoundary(error.response.data.error);
        } else {
          // Display the Axios error message.
          showBoundary(error);
        }
      })
      .catch(e => {
        console.error(e);
      });
  };

  return <BaseAssistant run={run} {...props} assistantType={AssistantType.Standard} />;
};
